import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ChevronLeft, Heart, MessageCircle, Eye, Calendar, User, 
  Send, Trash2, Loader2, ThumbsUp,
  Edit, X, MessagesSquare, MessageSquare, ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import { AuthorProfilePopover } from "../components/AuthorProfilePopover";
import CommunitySidebar from "../components/community/CommunitySidebar";
import CommunityRightRail from "../components/community/CommunityRightRail";
import { toFlowedText } from "../utils/postText";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category options for edit modal — 새 SNS 13개 카테고리 (Navigation/CommunitySidebar 와 일치)
const CATEGORIES = [
  { value: "home", label: "홈피드" },
  { value: "relationships", label: "연애 & 인간관계" },
  { value: "travel", label: "여행" },
  { value: "health", label: "건강" },
  { value: "hobby", label: "취미" },
  { value: "retirement", label: "노후 & 인생2막" },
  { value: "free", label: "자유게시판" },
  { value: "today_moment", label: "오늘의 하루" },
  { value: "anonymous_advice", label: "익명 고민상담" },
];

export const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});

  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return date.toLocaleDateString("ko-KR");
  };

  // Get initials from username
  const getInitials = (name) => {
    if (!name) return "?";
    return name.slice(0, 2);
  };

  useEffect(() => {
    fetchPost();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  useEffect(() => {
    if (post && user) {
      setIsLiked(post.liked_by?.includes(user.id) || false);
    }
  }, [post, user]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}`);
      if (!response.ok) throw new Error("게시글을 찾을 수 없습니다.");
      const data = await response.json();
      setPost(data);
    } catch (error) {
      console.error("Failed to fetch post:", error);
      navigate("/community");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}/like`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setPost(prev => ({ ...prev, likes: data.likes }));
      }
    } catch (error) {
      console.error("Failed to like post:", error);
    }
  };

  const handleSubmitComment = async (e, parentId = null) => {
    e.preventDefault();
    const commentText = parentId ? replyContent : newComment;
    if (!commentText.trim()) return;

    setIsSubmitting(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const body = { content: commentText.trim() };
      if (parentId) body.parent_id = parentId;

      const response = await fetch(`${API_URL}/api/posts/${postId}/comments`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const comment = await response.json();
        setComments(prev => [...prev, comment]);
        if (parentId) {
          setReplyContent("");
          setReplyingTo(null);
          // Update parent comment's replies_count locally
          setComments(prev => prev.map(c => 
            c.id === parentId ? { ...c, replies_count: (c.replies_count || 0) + 1 } : c
          ));
        } else {
          setNewComment("");
        }
        setPost(prev => ({ ...prev, comments: (prev.comments || 0) + 1 }));
      }
    } catch (error) {
      console.error("Failed to create comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("댓글을 삭제하시겠습니까?")) return;

    try {
      // Find the comment to check if it has replies
      const comment = comments.find(c => c.id === commentId);
      const repliesCount = comments.filter(c => c.parent_id === commentId).length;
      
      const response = await fetch(`${API_URL}/api/posts/${postId}/comments/${commentId}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Remove comment and all its replies
        setComments(prev => prev.filter(c => c.id !== commentId && c.parent_id !== commentId));
        // Update parent's replies_count if this was a reply
        if (comment?.parent_id) {
          setComments(prev => prev.map(c => 
            c.id === comment.parent_id ? { ...c, replies_count: Math.max(0, (c.replies_count || 0) - 1) } : c
          ));
        }
        // Decrease post comment count by 1 + replies deleted
        setPost(prev => ({ ...prev, comments: Math.max(0, (prev.comments || 0) - 1 - repliesCount) }));
      }
    } catch (error) {
      console.error("Failed to delete comment:", error);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!isAuthenticated) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}/comments/${commentId}/like`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setComments(prev => prev.map(c => 
          c.id === commentId 
            ? { ...c, likes: data.likes, liked_by: data.liked ? [...(c.liked_by || []), user.id] : (c.liked_by || []).filter(id => id !== user.id) }
            : c
        ));
      }
    } catch (error) {
      console.error("Failed to like comment:", error);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm("정말 이 게시글을 삭제하시겠습니까?\n삭제된 게시글은 복구할 수 없습니다.")) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (response.ok) {
        alert("게시글이 삭제되었습니다.");
        navigate("/community");
      } else {
        const data = await response.json();
        alert(data.detail || "게시글 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("Failed to delete post:", error);
      alert("게시글 삭제에 실패했습니다.");
    } finally {
      setIsDeleting(false);
    }
  };

  const isAuthor = user?.id === post?.author_id;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-[#F4F2EE] pt-20" data-testid="post-detail-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
        <div className="flex gap-5 items-start">
          {/* === Left Sidebar (new SNS categories) === */}
          <div className="hidden lg:block sticky top-20 self-start max-h-[calc(100vh-5.5rem)] overflow-y-auto pr-1 scrollbar-thin">
            <CommunitySidebar
              activeCategory="home"
              activeSub={null}
              onSelectCategory={(catId, subId) => {
                const params = new URLSearchParams();
                if (catId) params.set("cat", catId);
                if (subId) params.set("sub", subId);
                navigate(`/community${params.toString() ? `?${params.toString()}` : ""}`);
              }}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0 max-w-3xl mx-auto lg:mx-0">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors mb-4"
              data-testid="post-back-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              돌아가기
            </button>
        {/* Post Content */}
        <article className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          {/* Post Header */}
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-burgundy-500 font-medium bg-burgundy-50 px-2 py-1 rounded">{post.categoryName}</span>
              {/* Edit/Delete buttons for author */}
              {isAuthor && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-stone-600 hover:text-burgundy-600 hover:bg-burgundy-50 rounded-lg transition-colors"
                    data-testid="edit-post-btn"
                  >
                    <Edit className="w-4 h-4" />
                    수정
                  </button>
                  <button
                    onClick={handleDeletePost}
                    disabled={isDeleting}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-stone-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    data-testid="delete-post-btn"
                  >
                    {isDeleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    삭제
                  </button>
                </div>
              )}
            </div>
            <h1 className="text-2xl font-bold text-stone-800 mb-4" data-testid="post-title">
              {post.title}
            </h1>
            <div className="flex items-center justify-between text-sm text-stone-500">
              <div className="flex items-center gap-4">
                <AuthorProfilePopover authorId={post.author_id} authorName={post.author} authorMembership={post.author_membership} position="left">
                  <span className="flex items-center gap-1 hover:text-burgundy-600 transition-colors cursor-pointer" data-testid="post-author-name">
                    <User className="w-4 h-4" />
                    {post.author}
                  </span>
                </AuthorProfilePopover>
                {post.author_membership && post.author_membership !== 'basic' && (
                  <MembershipBadge membership={post.author_membership} size="md" />
                )}
                <DocumentVerifiedBadge verified={post.author_doc_verified} size="md" />
                {/* Chat with author button */}
                {isAuthenticated && post.author_id && post.author_id !== user?.id && (
                  <button
                    onClick={() => navigate(`/chat?userId=${post.author_id}&userName=${encodeURIComponent(post.author)}`)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-burgundy-50 text-burgundy-600 rounded-full hover:bg-burgundy-100 transition-colors"
                    data-testid="chat-with-author-btn"
                  >
                    <MessagesSquare className="w-3 h-3" />
                    채팅하기
                  </button>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {post.date}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {post.views}
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  {post.likes}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  {post.comments}
                </span>
              </div>
            </div>
          </div>

          {/* Post Body */}
          <div className="p-6">
            <div className="max-w-none text-[17px] sm:text-[18px] text-stone-800" data-testid="post-content">
              {String(post.content || "").split(/\n{2,}/).map((para, i) => (
                <p key={i} className="whitespace-pre-line leading-[1.8] mb-4 last:mb-0 break-words">
                  {para}
                </p>
              ))}
            </div>

            {/* Images */}
            {post.images?.length > 0 && (
              <div className="mt-6 grid grid-cols-2 gap-4">
                {post.images.map((img, index) => {
                  const pos = post.image_positions?.[index] || "center";
                  const objPos = pos === "top" ? "center top" : pos === "bottom" ? "center bottom" : "center";
                  return (
                    <img
                      key={index}
                      src={img.startsWith("http") ? img : `${API_URL}${img}`}
                      alt={`첨부 이미지 ${index + 1}`}
                      className="rounded-lg w-full object-cover"
                      style={{ objectPosition: objPos }}
                    />
                  );
                })}
              </div>
            )}

            {/* Videos */}
            {post.videos?.length > 0 && (
              <div className="mt-6 space-y-4">
                {post.videos.map((video, index) => (
                  <video 
                    key={index}
                    src={`${API_URL}${video}`}
                    controls
                    className="rounded-lg w-full"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Like Button */}
          <div className="px-6 pb-6">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                isLiked 
                  ? "bg-rose-100 text-rose-600 border-2 border-rose-300" 
                  : "bg-stone-100 text-stone-600 border-2 border-stone-200 hover:border-rose-300 hover:bg-rose-50"
              }`}
              data-testid="like-button"
            >
              <Heart className={`w-5 h-5 ${isLiked ? "fill-current" : ""}`} />
              <span className="font-medium">좋아요 {post.likes}</span>
            </button>
          </div>
        </article>

        {/* Comments Section */}
        <section className="mt-8 bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          <div className="p-6 border-b border-stone-100">
            <h2 className="font-bold text-lg text-stone-800 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-burgundy-500" />
              댓글 {comments.length}개
            </h2>
          </div>

          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} className="p-6 border-b border-stone-100">
            <div className="flex gap-4">
              {/* User Avatar */}
              <div className="w-14 h-14 rounded-full bg-slate-400 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">
                  {isAuthenticated ? getInitials(user?.username) : "?"}
                </span>
              </div>
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={isAuthenticated ? "댓글을 입력하세요..." : "로그인 후 댓글을 작성할 수 있습니다."}
                  className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 resize-none min-h-[80px] text-base"
                  data-testid="comment-input"
                />
                <div className="flex justify-end mt-3">
                  <Button
                    type="submit"
                    disabled={isSubmitting || !newComment.trim()}
                    className="btn-primary px-6 py-2"
                    data-testid="submit-comment-btn"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        댓글 작성
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </form>

          {/* Comments List */}
          <div className="divide-y divide-stone-100">
            {comments.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                아직 댓글이 없습니다. 첫 댓글을 남겨보세요!
              </div>
            ) : (
              // Filter only top-level comments (no parent_id)
              comments.filter(c => !c.parent_id).map((comment) => {
                const replies = comments.filter(c => c.parent_id === comment.id);
                const isExpanded = expandedReplies[comment.id];
                
                return (
                  <div key={comment.id} data-testid={`comment-${comment.id}`}>
                    {/* Main Comment */}
                    <div className="p-6">
                      <div className="flex gap-4">
                        {/* Profile Avatar with Initials */}
                        <AuthorProfilePopover authorId={comment.author_id} authorName={comment.author} authorMembership={comment.author_membership}>
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-burgundy-300 transition-all cursor-pointer ${
                            comment.author_membership === 'vip' ? 'bg-purple-400' :
                            comment.author_membership === 'premium' ? 'bg-amber-400' :
                            comment.author_membership === 'regular' ? 'bg-emerald-400' : 'bg-slate-400'
                          }`}>
                            <span className="text-white font-bold text-lg">
                              {getInitials(comment.author)}
                            </span>
                          </div>
                        </AuthorProfilePopover>
                        
                        <div className="flex-1">
                          {/* Author Info */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-stone-500 text-base">@{comment.author}</span>
                            {comment.author_membership && comment.author_membership !== 'basic' && (
                              <MembershipBadge membership={comment.author_membership} size="sm" />
                            )}
                            <DocumentVerifiedBadge verified={comment.author_doc_verified} size="sm" />
                            <span className="text-stone-400">·</span>
                            <span className="text-stone-400 text-base">
                              {getRelativeTime(comment.created_at)}
                            </span>
                          </div>
                          
                          {/* Comment Content */}
                          <p className="text-stone-800 text-lg leading-relaxed mb-4">
                            {comment.content}
                          </p>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center gap-6 text-stone-500">
                            {/* Reply Button */}
                            <button
                              onClick={() => {
                                setReplyingTo(replyingTo === comment.id ? null : comment.id);
                                setReplyContent("");
                              }}
                              className={`flex items-center gap-2 transition-colors text-base ${
                                replyingTo === comment.id ? "text-burgundy-500" : "hover:text-burgundy-500"
                              }`}
                              data-testid={`reply-btn-${comment.id}`}
                            >
                              <MessageSquare className="w-5 h-5" />
                              <span>Reply</span>
                            </button>
                            
                            {/* Replies Count */}
                            {replies.length > 0 && (
                              <button 
                                onClick={() => setExpandedReplies(prev => ({ ...prev, [comment.id]: !prev[comment.id] }))}
                                className="flex items-center gap-2 hover:text-burgundy-500 transition-colors text-base"
                                data-testid={`toggle-replies-${comment.id}`}
                              >
                                <span>{replies.length} replies</span>
                                <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                              </button>
                            )}
                            
                            {/* Like Button */}
                            <button
                              onClick={() => handleLikeComment(comment.id)}
                              className={`flex items-center gap-2 transition-colors text-base ${
                                comment.liked_by?.includes(user?.id)
                                  ? "text-rose-500"
                                  : "hover:text-rose-500"
                              }`}
                            >
                              <ThumbsUp className={`w-5 h-5 ${comment.liked_by?.includes(user?.id) ? "fill-current" : ""}`} />
                              <span>{comment.likes || 0}</span>
                            </button>

                            {/* 인연 확인 버튼 (본인 댓글이 아닐 때) */}
                            {isAuthenticated && user?.id !== comment.author_id && comment.author_id && (
                              <button
                                onClick={() => navigate(`/community/author/${comment.author_id}`)}
                                className="flex items-center gap-1.5 text-burgundy-400 hover:text-burgundy-600 transition-colors text-sm ml-1"
                                data-testid={`check-profile-${comment.id}`}
                              >
                                <Heart className="w-4 h-4" />
                                <span>인연 확인</span>
                              </button>
                            )}
                            
                            {/* Delete Button (only for own comments) */}
                            {user?.id === comment.author_id && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="flex items-center gap-2 text-stone-400 hover:text-red-500 transition-colors text-base ml-auto"
                                data-testid={`delete-comment-${comment.id}`}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>

                          {/* Reply Form */}
                          {replyingTo === comment.id && (
                            <form 
                              onSubmit={(e) => handleSubmitComment(e, comment.id)} 
                              className="mt-4 flex gap-3"
                              data-testid={`reply-form-${comment.id}`}
                            >
                              <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-bold text-sm">
                                  {isAuthenticated ? getInitials(user?.username) : "?"}
                                </span>
                              </div>
                              <div className="flex-1">
                                <textarea
                                  value={replyContent}
                                  onChange={(e) => setReplyContent(e.target.value)}
                                  placeholder={isAuthenticated ? `@${comment.author}님에게 답글 작성...` : "로그인 후 답글을 작성할 수 있습니다."}
                                  className="w-full px-3 py-2 bg-stone-50 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 resize-none min-h-[60px] text-sm"
                                  data-testid={`reply-input-${comment.id}`}
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setReplyingTo(null);
                                      setReplyContent("");
                                    }}
                                    className="border-stone-300 text-stone-600"
                                  >
                                    취소
                                  </Button>
                                  <Button
                                    type="submit"
                                    size="sm"
                                    disabled={isSubmitting || !replyContent.trim()}
                                    className="btn-primary"
                                    data-testid={`submit-reply-${comment.id}`}
                                  >
                                    {isSubmitting ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      "답글"
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </form>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Replies */}
                    {isExpanded && replies.length > 0 && (
                      <div className="bg-stone-50 border-l-4 border-burgundy-200">
                        {replies.map((reply) => (
                          <div key={reply.id} className="p-4 pl-8" data-testid={`reply-${reply.id}`}>
                            <div className="flex gap-3">
                              {/* Reply Avatar */}
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                reply.author_membership === 'vip' ? 'bg-purple-400' :
                                reply.author_membership === 'premium' ? 'bg-amber-400' :
                                reply.author_membership === 'regular' ? 'bg-emerald-400' : 'bg-slate-300'
                              }`}>
                                <span className="text-white font-bold text-sm">
                                  {getInitials(reply.author)}
                                </span>
                              </div>
                              
                              <div className="flex-1">
                                {/* Reply Author Info */}
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-stone-500 text-sm">@{reply.author}</span>
                                  {reply.author_membership && reply.author_membership !== 'basic' && (
                                    <MembershipBadge membership={reply.author_membership} size="sm" />
                                  )}
                                  <DocumentVerifiedBadge verified={reply.author_doc_verified} size="sm" />
                                  <span className="text-stone-400 text-xs">·</span>
                                  <span className="text-stone-400 text-sm">
                                    {getRelativeTime(reply.created_at)}
                                  </span>
                                </div>
                                
                                {/* Reply Content */}
                                <p className="text-stone-700 text-base leading-relaxed mb-2">
                                  {reply.content}
                                </p>
                                
                                {/* Reply Actions */}
                                <div className="flex items-center gap-4 text-stone-400 text-sm">
                                  {/* Like Button */}
                                  <button
                                    onClick={() => handleLikeComment(reply.id)}
                                    className={`flex items-center gap-1 transition-colors ${
                                      reply.liked_by?.includes(user?.id)
                                        ? "text-rose-500"
                                        : "hover:text-rose-500"
                                    }`}
                                  >
                                    <ThumbsUp className={`w-4 h-4 ${reply.liked_by?.includes(user?.id) ? "fill-current" : ""}`} />
                                    <span>{reply.likes || 0}</span>
                                  </button>
                                  
                                  {/* Delete Button (only for own replies) */}
                                  {user?.id === reply.author_id && (
                                    <button
                                      onClick={() => handleDeleteComment(reply.id)}
                                      className="flex items-center gap-1 hover:text-red-500 transition-colors ml-auto"
                                      data-testid={`delete-reply-${reply.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Link
            to="/community"
            className="inline-flex items-center gap-2 px-6 py-3 bg-stone-100 text-stone-600 rounded-full hover:bg-stone-200 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            목록으로 돌아가기
          </Link>
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <EditPostModal
            post={post}
            token={token}
            onClose={() => setShowEditModal(false)}
            onUpdated={(updatedPost) => {
              setPost(updatedPost);
              setShowEditModal(false);
            }}
          />
        )}
      </div>
      {/* === Right Rail (new SNS — desktop xl only) === */}
      <div className="hidden xl:block sticky top-20 self-start max-h-[calc(100vh-5.5rem)] overflow-y-auto pr-1 scrollbar-thin">
        <CommunityRightRail />
      </div>
      </div>
      </div>
    </div>
  );
};

// Edit Post Modal Component
const EditPostModal = ({ post, token, onClose, onUpdated }) => {
  const [category, setCategory] = useState(post.category);
  const [title, setTitle] = useState(post.title);
  const [content, setContent] = useState(post.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError("제목을 입력해주세요.");
      return;
    }
    if (!content.trim()) {
      setError("내용을 입력해주세요.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/api/posts/${post.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          category,
          title: title.trim(),
          content: content.trim(),
        }),
      });

      if (response.ok) {
        const updatedPost = await response.json();
        onUpdated(updatedPost);
      } else {
        const data = await response.json();
        setError(data.detail || "게시글 수정에 실패했습니다.");
      }
    } catch (err) {
      setError("게시글 수정에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-stone-200 flex items-center justify-between flex-shrink-0">
          <h2 className="font-bold text-lg text-stone-800">게시글 수정</h2>
          <button
            onClick={onClose}
            className="p-2 text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">카테고리</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                data-testid="edit-category"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
                className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                data-testid="edit-title"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="내용을 입력하세요"
                className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 min-h-[200px] resize-none"
                data-testid="edit-content"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-stone-200 flex justify-end gap-3 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-stone-300 text-stone-600"
            >
              취소
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex items-center gap-2"
              data-testid="submit-edit-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Edit className="w-4 h-4" />
                  수정하기
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostDetailPage;
